/**
 * Class representing drag and drop functionality.
 */
export default class DragAndDrop {
  /**
   * Initializes the drop zone with the given ID.
   * @param {string} id - The ID of the drop zone element.
   */
  initDropZone(id: string): void {
    const elem = document.getElementById(id);
    if (!elem) {
      console.error(`Element with id ${id} not found.`);
      return;
    }

    // Prevent default behavior for dragover event
    elem.addEventListener('dragover', (event: DragEvent) => event.preventDefault());

    // Set data transfer data on dragstart event
    elem.addEventListener('dragstart', (event: DragEvent) => {
      if (event.dataTransfer && event.target instanceof HTMLElement) {
        event.dataTransfer.setData('text/plain', event.target.id);
      }
    });
  }

  /**
   * Makes drop zones not relative by setting their position to static or unset.
   */
  makeDropZonesNotRelative(): void {
    // Get all drop items with index -1 and set their position to static
    const firstDropItems = Array.from(document.getElementsByClassName('fe-drop-item'))
      .filter(x => x.getAttribute('index') === "-1") as HTMLElement[];
    for (let dropItem of firstDropItems) {
      dropItem.style.position = 'static';
    }

    // Get all drop zones and set their position to unset
    const dropZones = document.getElementsByClassName('fe-drop-zone') as HTMLCollectionOf<HTMLElement>;
    for (let dropZone of dropZones) {
      dropZone.style.position = 'unset';
    }
  }

  /**
   * Gets the identifier of the drop zone at the specified position.
   * @param {number} x - The x-coordinate of the position.
   * @param {number} y - The y-coordinate of the position.
   * @returns {string} The identifier of the drop zone, or an empty string if no drop zone is found.
   */
  getDropZoneIdentifierOnPosition(x: number, y: number): string {
    // Get all elements at the specified position
    const elems = document.elementsFromPoint(x, y) as HTMLElement[];

    // Filter elements to find drop zones
    const dropZones = elems.filter(e => e.classList.contains('fe-drop-zone'));
    const dropZone = dropZones[0];

    // Return the identifier attribute of the drop zone, or an empty string if not found
    if (dropZone) {
      return dropZone.getAttribute('identifier') || "";
    }
    return "";
  }

  /**
   * Moves an item by the specified difference in x and y coordinates.
   * @param {string} id - The ID of the element to move.
   * @param {number} dx - The difference in the x-coordinate.
   * @param {number} dy - The difference in the y-coordinate.
   */
  moveItemByDifference(id: string, dx: number, dy: number): void {
    const elem = document.getElementById(id);
    if (!elem) {
      console.error(`Element with id ${id} not found.`);
      return;
    }

    // Keep the accumulated dragged position in the data-x/data-y attributes
    const tx = (parseFloat(elem.getAttribute('data-x') || '0') + dx);
    const ty = (parseFloat(elem.getAttribute('data-y') || '0') + dy);

    // Translate the element
    elem.style.transform = `translate3d(${tx}px, ${ty}px, 10px)`;

    // Update the position attributes
    elem.setAttribute('data-x', tx.toString());
    elem.setAttribute('data-y', ty.toString());
  }

  /**
   * Resets the position of an item to its original state.
   * @param {string} id - The ID of the element to reset.
   */
  resetItem(id: string): void {
    const elem = document.getElementById(id);
    if (!elem) {
      console.error(`Element with id ${id} not found.`);
      return;
    }

    // Reset the transform and position attributes
    elem.style.transform = 'translate3d(0px, 0px, 10px)';
    elem.setAttribute('data-x', '0');
    elem.setAttribute('data-y', '0');
  }
}